import { loadScript } from '@/modules/upload/cloud/loadScript'

// The parts of Google Identity Services and the Google Picker this app uses.
// https://developers.google.com/identity/oauth2/web/reference/js-reference
// https://developers.google.com/workspace/drive/picker/reference
export interface GoogleTokenResponse {
  access_token?: string
  error?: string
}

// type is popup_failed_to_open, popup_closed or unknown.
export interface GoogleSignInFailure {
  type: string
}

export interface GoogleTokenClientConfig {
  client_id: string
  scope: string
  callback: (response: GoogleTokenResponse) => void
  error_callback: (failure: GoogleSignInFailure) => void
}

export interface GoogleOAuth2 {
  initTokenClient: (config: GoogleTokenClientConfig) => {
    requestAccessToken: () => void
  }
  hasGrantedAllScopes: (response: GoogleTokenResponse, scope: string) => boolean
  revoke: (accessToken: string, done: () => void) => void
}

export interface GooglePickerDocument {
  id: string
  name: string
  sizeBytes?: number | string
}

export interface GooglePickerResponse {
  action: string
  docs?: GooglePickerDocument[]
}

export interface GoogleDocsView {
  setMimeTypes: (mimeTypes: string) => GoogleDocsView
  setEnableDrives: (enabled: boolean) => GoogleDocsView
  setIncludeFolders: (included: boolean) => GoogleDocsView
  setSelectFolderEnabled: (enabled: boolean) => GoogleDocsView
  setOwnedByMe: (ownedByMe: boolean) => GoogleDocsView
  setParent: (parentId: string) => GoogleDocsView
}

export interface GooglePickerBuilder {
  addView: (view: GoogleDocsView) => GooglePickerBuilder
  enableFeature: (feature: string) => GooglePickerBuilder
  setOAuthToken: (token: string) => GooglePickerBuilder
  setDeveloperKey: (key: string) => GooglePickerBuilder
  setAppId: (appId: string) => GooglePickerBuilder
  setCallback: (
    callback: (response: GooglePickerResponse) => void,
  ) => GooglePickerBuilder
  build: () => { setVisible: (visible: boolean) => void }
}

export interface GooglePicker {
  Action: { PICKED: string; CANCEL: string; ERROR: string }
  Feature: { MULTISELECT_ENABLED: string }
  ViewId: { DOCS: string }
  DocsView: new (viewId: string) => GoogleDocsView
  PickerBuilder: new () => GooglePickerBuilder
}

export interface GoogleApiLoader {
  load: (
    library: string,
    handlers: { callback: () => void; onerror: () => void },
  ) => void
}

export interface GoogleGlobals {
  google?: { accounts?: { oauth2: GoogleOAuth2 }; picker?: GooglePicker }
  gapi?: GoogleApiLoader
}

// Not a Window augmentation: another package's types already declare a global google namespace.
export const googleGlobals = () => window as unknown as GoogleGlobals

export const IDENTITY_SERVICES_SCRIPT = {
  id: 'google-identity-services',
  src: 'https://accounts.google.com/gsi/client',
}
export const GOOGLE_API_SCRIPT = {
  id: 'google-api',
  src: 'https://apis.google.com/js/api.js',
}

const loadIdentityServices = () =>
  googleGlobals().google?.accounts?.oauth2
    ? Promise.resolve(true)
    : loadScript(IDENTITY_SERVICES_SCRIPT)

const loadPickerLibrary = (loader: GoogleApiLoader) =>
  new Promise<boolean>((resolve) =>
    loader.load('picker', {
      callback: () => resolve(true),
      onerror: () => resolve(false),
    }),
  )

const loadApiLoader = async () => {
  const isLoaded = Boolean(googleGlobals().gapi)
  if (!isLoaded && !(await loadScript(GOOGLE_API_SCRIPT))) return null
  return googleGlobals().gapi ?? null
}

const loadPicker = async () => {
  if (googleGlobals().google?.picker) return true
  const loader = await loadApiLoader()
  return loader ? loadPickerLibrary(loader) : false
}

// Resolves false when either script is blocked or fails: the Google Drive button then stays hidden.
export const loadGoogleScripts = async () => {
  const [hasIdentityServices, hasPicker] = await Promise.all([
    loadIdentityServices(),
    loadPicker(),
  ])
  return hasIdentityServices && hasPicker
}

export const loadedOAuth2 = () => {
  const oauth2 = googleGlobals().google?.accounts?.oauth2
  if (!oauth2) throw new Error('Google Identity Services is not loaded')
  return oauth2
}

export const loadedPicker = () => {
  const picker = googleGlobals().google?.picker
  if (!picker) throw new Error('Google Picker is not loaded')
  return picker
}
